import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Building2,
  Calculator,
  CheckCircle2,
  FileCheck2,
  HardHat,
  Lock,
  Play,
  Shield,
  Tractor,
  Users,
} from "lucide-react";
import {
  APP_DESCRIPTION,
  APP_HERO,
  APP_NAME,
  APP_SLOGAN,
  APP_TAGLINE,
  SUPPORT_EMAIL,
} from "@crewledger/shared/constants";
import { Button } from "@/components/ui/button";
import { BlueprintBackground } from "@/components/design";
import { GetStartedButton } from "@/features/marketing/get-started-button";

function BetaAccessNote({ className }: { className?: string }) {
  return (
    <p className={className}>
      Private beta access only. Request access if you are not yet invited.
    </p>
  );
}

function LandingNav() {
  return (
    <header className="sticky top-0 z-50 border-b border-white/10 bg-navy-deep/95 backdrop-blur">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-4 md:px-6">
        <Link to="/" className="flex items-center gap-2 font-semibold text-white">
          <HardHat className="h-6 w-6 text-orange-construction" />
          {APP_NAME}
        </Link>
        <nav className="hidden items-center gap-6 text-sm text-white/80 md:flex">
          <a href="#product" className="hover:text-white">
            Product
          </a>
          <a href="#pricing" className="hover:text-white">
            Pricing
          </a>
          <Link to="/demo" className="hover:text-white">
            Demo
          </Link>
          <GetStartedButton
            size="sm"
            variant="ghost"
            className="h-auto px-0 text-white/80 hover:bg-transparent hover:text-white"
          >
            Get Started
          </GetStartedButton>
        </nav>
        <div className="flex items-center gap-2 md:hidden">
          <Link to="/demo">
            <Button variant="outline" size="sm" className="border-white/20 bg-transparent text-white hover:bg-white/10">
              Demo
            </Button>
          </Link>
          <GetStartedButton size="sm">Get Started</GetStartedButton>
        </div>
      </div>
    </header>
  );
}

function SectionHeading({
  eyebrow,
  title,
  description,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
}) {
  return (
    <div className="mx-auto max-w-3xl text-center">
      {eyebrow ? (
        <p className="text-sm font-semibold uppercase tracking-wider text-orange-construction">{eyebrow}</p>
      ) : null}
      <h2 className="mt-2 text-3xl font-bold tracking-tight text-navy-deep md:text-4xl">{title}</h2>
      {description ? <p className="mt-4 text-lg text-muted-foreground">{description}</p> : null}
    </div>
  );
}

function FeatureCard({
  icon: Icon,
  title,
  description,
}: {
  icon: typeof HardHat;
  title: string;
  description: string;
}) {
  return (
    <div className="rounded-xl border bg-card-white p-6 shadow-[var(--shadow-card)]">
      <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-navy-deep/5 text-navy-deep">
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="mt-4 text-lg font-semibold text-navy-deep">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{description}</p>
    </div>
  );
}

function PricingTier({
  name,
  price,
  description,
  features,
  highlighted,
}: {
  name: string;
  price: string;
  description: string;
  features: string[];
  highlighted?: boolean;
}) {
  return (
    <div
      className={`rounded-xl border p-6 ${
        highlighted
          ? "border-orange-construction bg-navy-deep text-white shadow-[var(--shadow-elevated)]"
          : "bg-card-white shadow-[var(--shadow-card)]"
      }`}
    >
      <h3 className="text-lg font-semibold">{name}</h3>
      <p className={`mt-1 text-sm ${highlighted ? "text-white/70" : "text-muted-foreground"}`}>
        {description}
      </p>
      <p className="mt-4 text-3xl font-bold">{price}</p>
      <ul className="mt-6 space-y-2 text-sm">
        {features.map((feature) => (
          <li key={feature} className="flex items-start gap-2">
            <CheckCircle2
              className={`mt-0.5 h-4 w-4 shrink-0 ${highlighted ? "text-orange-construction" : "text-green-approved"}`}
            />
            <span className={highlighted ? "text-white/90" : "text-muted-foreground"}>{feature}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function LandingPage() {
  useEffect(() => {
    document.title = `${APP_NAME} — ${APP_TAGLINE}`;
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <LandingNav />

      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,138,0,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,138,0,0.08) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
        <div className="relative mx-auto max-w-6xl px-4 py-20 md:px-6 md:py-28">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wider text-orange-construction">
              {APP_SLOGAN}
            </p>
            <h1 className="mt-4 text-4xl font-bold leading-tight tracking-tight md:text-5xl lg:text-6xl">
              {APP_NAME}
              <span className="block text-2xl font-semibold text-white/90 md:text-3xl">{APP_TAGLINE}</span>
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-white/80 md:text-xl">{APP_HERO}</p>
            <p className="mt-4 text-base text-white/70">
              Log daily labor, track equipment usage, apply complex pay rules, approve pay periods, and export
              payroll-ready records from one field command center.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <GetStartedButton size="lg" className="gap-2">
                Get Started
                <ArrowRight className="h-4 w-4" />
              </GetStartedButton>
              <Link to="/demo">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white/25 bg-transparent text-white hover:bg-white/10"
                >
                  <Play className="mr-1 h-4 w-4" />
                  View Demo
                </Button>
              </Link>
            </div>
            <BetaAccessNote className="mt-4 max-w-xl text-sm text-white/60" />
          </div>
        </div>
      </section>

      <section id="product" className="py-20 md:py-24">
        <div className="mx-auto max-w-6xl px-4 md:px-6">
          <SectionHeading
            eyebrow="Platform"
            title="The field command center for construction operations"
            description={APP_DESCRIPTION}
          />
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <FeatureCard
              icon={Building2}
              title="Built for construction sites"
              description="Multi-site crews, trades, and foremen — not generic HR time clocks. Site-scoped roles and crew templates match how GCs operate."
            />
            <FeatureCard
              icon={Users}
              title="Daily labor entry"
              description="Site managers log hours, job codes, cost codes, and flags per worker. Werkio Rules Engine applies OT, premiums, and pay logic automatically."
            />
            <FeatureCard
              icon={Tractor}
              title="Equipment usage tracking"
              description="Assign excavators, cranes, rentals, and tools to daily entries. Equipment cost flows into site reports separately from gross pay."
            />
            <FeatureCard
              icon={Calculator}
              title="Werkio Rules Engine"
              description="Daily OT, weekly thresholds, weekend multipliers, hazard premiums, and holiday pay — stack or prioritize per worker profile."
            />
            <FeatureCard
              icon={FileCheck2}
              title="Payroll-ready exports"
              description="Lock the pay period, export CSV/PDF, and hand off to your payroll processor. Permission-checked downloads stored privately in R2."
            />
            <FeatureCard
              icon={Lock}
              title="Werkio Audit Trail"
              description="Immutable audit log, period lock workflow, correction requests with reasons — audit-ready for payroll review."
            />
            <FeatureCard
              icon={Shield}
              title="Werkio Proof Layer"
              description="Werkio never publishes employee names, pay rates, raw hours, notes, or equipment details onchain. Optional proof uses hashed pay-period records and Merkle roots only."
            />
            <FeatureCard
              icon={HardHat}
              title="Privacy-safe employee IDs"
              description="Display names for managers. Employee IDs for payroll. Role-based access hides rates from field staff."
            />
          </div>
        </div>
      </section>

      <section className="border-y bg-bg-light py-20 md:py-24">
        <div className="mx-auto max-w-6xl px-4 md:px-6">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <SectionHeading
                eyebrow="Built for construction"
                title="Trusted by foremen. Built for payroll managers."
                description="Werkio is the field command center for construction time, equipment, pay rules, payroll-ready records, audit logs, and optional privacy-safe proof."
              />
              <ul className="mt-8 space-y-3 text-sm text-muted-foreground">
                {[
                  "500–5,000 workers across multiple active sites",
                  "Site-scoped roles for foremen and payroll managers",
                  "Crew templates with default job codes and equipment",
                  "Biweekly, weekly, or semi-monthly pay periods",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-orange-construction" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <BlueprintBackground className="rounded-2xl border p-6">
              <div className="grid gap-3 sm:grid-cols-2">
                {[
                  { label: "Active sites", value: "3" },
                  { label: "Workers today", value: "28" },
                  { label: "Hours this period", value: "1,840" },
                  { label: "Pending approvals", value: "12" },
                ].map((stat) => (
                  <div key={stat.label} className="rounded-lg border bg-card-white p-4">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">{stat.label}</p>
                    <p className="mt-1 text-2xl font-bold tabular-nums text-navy-deep">{stat.value}</p>
                  </div>
                ))}
              </div>
            </BlueprintBackground>
          </div>
        </div>
      </section>

      <section id="pricing" className="py-20 md:py-24">
        <div className="mx-auto max-w-6xl px-4 md:px-6">
          <SectionHeading
            eyebrow="Pricing"
            title="Pricing by active worker"
            description="Simple pricing based on active workers per pay period."
          />
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            <PricingTier
              name="Starter"
              price="$2/worker"
              description="Per active worker, per pay period"
              features={[
                "Daily field entry & pay rules",
                "Equipment tracking",
                "Pay period review & export",
                "Email support",
              ]}
            />
            <PricingTier
              name="Pro"
              price="$3/worker"
              description="Per active worker, per pay period"
              features={[
                "Everything in Starter",
                "Advanced reports & audit trail",
                "Role-based access control",
                "Priority onboarding",
              ]}
              highlighted
            />
            <PricingTier
              name="Enterprise"
              price="Custom"
              description="Large sites, integrations, and proof layer"
              features={[
                "Everything in Pro",
                "Large sites & custom integrations",
                "Advanced reports",
                "Werkio Proof Layer",
              ]}
            />
          </div>
        </div>
      </section>

      <section className="bg-navy-deep py-20 text-white md:py-24">
        <div className="mx-auto max-w-3xl px-4 text-center md:px-6">
          <h2 className="text-3xl font-bold tracking-tight md:text-4xl">
            Track the crew. Count the machines. Close payroll.
          </h2>
          <p className="mt-4 text-lg text-white/80">
            Explore the interactive demo with sample data, or get started with a blank beta workspace when
            you&apos;re on the allowlist.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <GetStartedButton size="lg">Get Started</GetStartedButton>
            <Link to="/demo">
              <Button size="lg" variant="outline" className="border-white/25 bg-transparent text-white hover:bg-white/10">
                View Demo
              </Button>
            </Link>
          </div>
          <BetaAccessNote className="mt-4 text-sm text-white/60" />
        </div>
      </section>

      <footer className="border-t py-8 text-center text-sm text-muted-foreground">
        <p>
          © {new Date().getFullYear()} {APP_NAME}. Built for construction payroll teams.
        </p>
        <p className="mt-1">
          Questions?{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="text-navy-deep hover:underline">
            {SUPPORT_EMAIL}
          </a>
        </p>
      </footer>
    </div>
  );
}
